/**
 * The admin portal's own API client.
 *
 * Deliberately not `@/api/client`. That one answers a 401 with a silent
 * `POST /auth/refresh` and retries, because a carrier's access token lives
 * fifteen minutes and a page left open reaches that routinely. The portal has
 * one token and no refresh endpoint at all (`app/core/platform_auth.py`), so
 * the same behaviour here would be a pointless request on every expiry and,
 * worse, would send the carrier's CSRF cookie - the two sessions share
 * nothing, and a client that reads both cookie sets is the first place they
 * would start to blur.
 *
 * A 401 means the session is over: say so once, and let the portal return to
 * its own sign-in page.
 */

export class PlatformError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

const BASE = '/api/v1/platform'

/** Fired when the portal's session is gone. `PlatformAuthProvider` listens. */
export const PLATFORM_SESSION_ENDED = 'ix:platform-session-ended'

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[2]) : null
}

/** The portal's own CSRF cookie, under whichever prefix the API gave it. */
function csrfToken(): string | null {
  return (
    readCookie('__Host-ix_admin_csrf') ??
    readCookie('__Secure-ix_admin_csrf') ??
    readCookie('ix_admin_csrf')
  )
}

type Options = {
  method?: string
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
}

// Sign-in's own 401 means "wrong password", not "your session ended", so it
// must not fire the event that bounces the portal to the login page it is on.
const CREDENTIALS = /^\/auth\/(login|logout)(\/|$|\?)/

export async function platformApi<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const { method = 'GET', body, query } = opts

  let url = `${BASE}${path}`
  if (query) {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
    }
    const s = qs.toString()
    if (s) url += `?${s}`
  }

  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = csrfToken()
    if (csrf) headers['X-CSRF-Token'] = csrf
  }

  const res = await fetch(url, {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (res.status === 401 && !CREDENTIALS.test(path)) {
    window.dispatchEvent(new Event(PLATFORM_SESSION_ENDED))
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let parsed: unknown = null
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      parsed = text
    }
  }

  if (!res.ok) {
    const detail =
      parsed && typeof parsed === 'object' && 'detail' in parsed
        ? (parsed as { detail: unknown }).detail
        : null
    const message =
      (Array.isArray(detail)
        ? detail
            .map((d) => String((d as { msg?: unknown })?.msg ?? '').replace(/^Value error, /, ''))
            .filter(Boolean)
            .join(' ')
        : detail !== null
          ? String(detail)
          : null) || res.statusText
    throw new PlatformError(res.status, message)
  }

  return parsed as T
}

export const pGet = <T,>(path: string, query?: Options['query']) => platformApi<T>(path, { query })
export const pPost = <T,>(path: string, body?: unknown) =>
  platformApi<T>(path, { method: 'POST', body })
export const pPatch = <T,>(path: string, body?: unknown) =>
  platformApi<T>(path, { method: 'PATCH', body })
export const pDel = <T,>(path: string) => platformApi<T>(path, { method: 'DELETE' })

export function message(e: unknown, fallback = 'Something went wrong.'): string {
  return e instanceof PlatformError ? e.message : fallback
}

// ── shapes the API publishes ──────────────────────────────────────

export interface PlatformAdminRow {
  id: string
  email: string
  fullName: string
  isActive: boolean
  mustChangePassword: boolean
  mfaEnabled: boolean
  lastLoginAt: string | null
  createdByName: string | null
  createdAt: string
  lockedUntil: string | null
}

export interface PlatformMe {
  admin: PlatformAdminRow
  activeAdmins: number
}

/** A correct password with two-factor on: no session yet, only this. */
export interface PlatformMfaChallenge {
  mfaRequired: true
  challenge: string
  expiresIn: number
}

export function isMfaChallenge(r: PlatformMe | PlatformMfaChallenge): r is PlatformMfaChallenge {
  return (r as PlatformMfaChallenge).mfaRequired === true
}

export interface OrgAdmin {
  id: string
  email: string
  fullName: string
  role: string
  isActive: boolean
  mustChangePassword: boolean
  lastLoginAt: string | null
}

export interface OrgRow {
  id: string
  name: string
  slug: string
  domain: string | null
  domainName: string | null
  subdomain: string | null
  baseUrl: string
  isActive: boolean
  deactivatedAt: string | null
  onboardingCompletedAt: string | null
  createdAt: string
  users: number
  activeUsers: number
  circulars: number
  admins: OrgAdmin[]
}

export interface Tenancy {
  baseDomain: string
  enabled: boolean
  adminHost: string
  reserved: string[]
}

export interface AuditEvent {
  id: string
  actorEmail: string
  actorName: string | null
  action: string
  organizationId: string | null
  organizationName: string | null
  payload: Record<string, unknown> | null
  ip: string | null
  createdAt: string
}
